import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { MdGarage } from 'react-icons/md';
import SafeIcon from '../common/SafeIcon';
import Logo from './Logo';
import UserDashboard from './UserDashboard';

const { FiMenu, FiX, FiPhone, FiPhoneCall, FiMessageCircle, FiShoppingBag, FiSend } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isHoveringItem, setIsHoveringItem] = useState(null);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [garageStatus, setGarageStatus] = useState('neutral');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    document.body.style.overflow = 'unset';
  }, [location]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const checkGarageStatus = useCallback(() => {
    const savedData = localStorage.getItem('bcb_user_vehicles');
    if (!savedData) {
      setGarageStatus('neutral');
      return;
    }
    try {
      const vehicles = JSON.parse(savedData);
      if (!Array.isArray(vehicles) || vehicles.length === 0) {
        setGarageStatus('neutral');
        return;
      }
      let hasError = false;
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      vehicles.forEach(v => {
        if (v.serviceDate) {
          const lastService = new Date(v.serviceDate);
          const nextService = new Date(lastService);
          nextService.setFullYear(lastService.getFullYear() + 1);
          nextService.setHours(0, 0, 0, 0);
          if (todayStart >= nextService) hasError = true;
        }
        if (v.tuvMonth && v.tuvYear) {
          const nextYear = parseInt(v.tuvYear) + 2;
          const nextMonth = parseInt(v.tuvMonth);
          if (currentYear > nextYear || (currentYear === nextYear && currentMonth > nextMonth)) {
            hasError = true;
          }
        }
      });
      setGarageStatus(hasError ? 'error' : 'success');
    } catch (e) {
      setGarageStatus('neutral');
    }
  }, []);

  useEffect(() => {
    checkGarageStatus();
    window.addEventListener('storage', checkGarageStatus);
    return () => window.removeEventListener('storage', checkGarageStatus);
  }, [checkGarageStatus]);

  const getGarageColorClass = () => {
    if (garageStatus === 'error') return 'text-red-500';
    if (garageStatus === 'success') return 'text-green-500';
    return 'text-orange-400';
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/news', label: 'News' },
    { path: '/ueber-uns', label: 'Über uns' },
    { path: '/leistungen', label: 'Leistungen' },
    { path: '/zu-verkaufen', label: 'Zu verkaufen' },
    { path: '/kontakt', label: 'Kontakt' }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled 
            ? 'bg-black/95 backdrop-blur-xl shadow-2xl py-3 border-b border-orange-500/20' 
            : 'bg-transparent py-6 lg:bg-transparent'
        }`}
      >
        {/* Mobile-only background when not scrolled for visibility */}
        <div className={`lg:hidden absolute inset-0 transition-opacity duration-300 ${scrolled ? 'opacity-0' : 'bg-black/40 backdrop-blur-sm opacity-100'}`} />

        <div className="max-w-[98rem] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-between items-center gap-4">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-shrink-0"
            >
              <Link to="/" className="block">
                <Logo className="h-10 xl:h-12" showText={true} variant="dark" />
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-4 xl:space-x-8">
              {navItems.map((item) => (
                <motion.div
                  key={item.path}
                  onHoverStart={() => setIsHoveringItem(item.path)}
                  onHoverEnd={() => setIsHoveringItem(null)}
                  className="relative group"
                >
                  <Link
                    to={item.path}
                    className={`relative text-xs xl:text-sm font-bold uppercase tracking-widest whitespace-nowrap transition-all duration-300 ${
                      location.pathname === item.path ? 'text-orange-500' : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    <span className="relative z-10">{item.label}</span>
                    <motion.span
                      className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-orange-600 to-orange-400 transition-all duration-500 ${
                        location.pathname === item.path || isHoveringItem === item.path ? 'w-full' : 'w-0'
                      }`}
                    />
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Header Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              <motion.a
                href="https://shop.bavaria-custom-bikes.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-full px-4 xl:px-6 py-2 bg-gradient-to-r from-orange-600 to-orange-500 text-white font-black text-[10px] xl:text-xs shadow-lg shadow-orange-500/20 uppercase tracking-widest whitespace-nowrap"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <SafeIcon icon={FiShoppingBag} className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
                  <span>Shop</span>
                </span>
              </motion.a>

              <motion.button
                onClick={() => setIsDashboardOpen(true)}
                className="group relative overflow-hidden rounded-full px-3 py-2 bg-gray-900/60 border border-orange-500/20"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <SafeIcon icon={MdGarage} className={`w-5 h-5 xl:w-6 xl:h-6 ${getGarageColorClass()}`} />
              </motion.button>

              <motion.a
                href="tel:017632543593"
                className="group relative overflow-hidden rounded-full px-4 xl:px-6 py-2 bg-gradient-to-r from-gray-800 to-gray-700 text-white font-black text-[10px] xl:text-xs border border-gray-600/50 uppercase tracking-widest"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <SafeIcon icon={FiPhoneCall} className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
                  <span>Anrufen</span>
                </span>
              </motion.a>
            </div>

            {/* Mobile Menu Toggle & Garage Button */}
            <div className="flex lg:hidden items-center gap-4">
              <motion.button
                onClick={() => setIsDashboardOpen(true)}
                className="p-2 bg-gray-800/80 backdrop-blur-md rounded-lg border border-gray-700"
                whileTap={{ scale: 0.9 }}
              >
                <SafeIcon icon={MdGarage} className={`w-6 h-6 ${getGarageColorClass()}`} />
              </motion.button>
              <motion.button
                onClick={toggleMenu}
                className="p-2 text-white bg-orange-600 rounded-lg shadow-lg shadow-orange-600/20"
                whileTap={{ scale: 0.9 }}
              >
                <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-7 h-7" />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile menu overlay - HELLER HINTERGRUND FÜR BESSERE LESBARKEIT */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="lg:hidden fixed inset-0 h-[100dvh] bg-gray-50 z-[60] flex flex-col pt-24 pb-12 px-6 overflow-y-auto"
            >
              {/* Close button */}
              <button 
                onClick={toggleMenu}
                className="absolute top-6 right-6 p-3 text-gray-900 bg-white shadow-xl rounded-full border border-gray-200 hover:bg-orange-600 hover:text-white transition-all"
              >
                <SafeIcon icon={FiX} className="w-8 h-8" />
              </button>

              <div className="flex flex-col items-center space-y-2 mb-12">
                {navItems.map((item, idx) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="w-full"
                  >
                    <Link
                      to={item.path}
                      className={`block w-full text-2xl font-black uppercase tracking-[0.2em] py-4 px-6 rounded-2xl transition-all ${
                        location.pathname === item.path 
                          ? 'text-orange-600 bg-orange-500/10' 
                          : 'text-gray-900 hover:text-orange-600 hover:bg-gray-100'
                      }`}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="mt-auto space-y-4">
                <motion.a
                  href="https://shop.bavaria-custom-bikes.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-4 text-lg font-black text-white uppercase tracking-widest bg-gradient-to-r from-orange-600 to-orange-500 px-8 py-5 rounded-2xl shadow-xl shadow-orange-500/30"
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon icon={FiShoppingBag} className="w-6 h-6" />
                  <span>Zum Online Shop</span>
                </motion.a>

                <div className="grid grid-cols-2 gap-4">
                  <motion.a
                    href="tel:017632543593"
                    className="flex items-center justify-center gap-3 text-sm font-black text-gray-900 uppercase tracking-widest border border-gray-200 bg-white shadow-sm px-4 py-4 rounded-2xl"
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiPhone} className="w-5 h-5 text-orange-500" />
                    <span>Anrufen</span>
                  </motion.a>
                  <Link
                    to="/kontakt"
                    className="flex items-center justify-center gap-3 text-sm font-black text-white uppercase tracking-widest bg-gray-900 px-4 py-4 rounded-2xl"
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-orange-500" />
                    <span>Kontakt</span>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isDashboardOpen && (
            <UserDashboard onClose={() => setIsDashboardOpen(false)} onUpdate={checkGarageStatus} />
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar;